import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  EmailAuthProvider,
  getAuth,
  getMultiFactorResolver,
  multiFactor,
  MultiFactorResolver,
  reauthenticateWithCredential,
  TotpMultiFactorGenerator,
  TotpSecret
} from 'firebase/auth';
import QRCode from 'qrcode';

import { MfaQrStep, MfaReAuthForm } from '@/components/common';
import { Button, Label, LABEL_TYPE } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { queryClient } from '@/configuration/react-query';
import { useAuth } from '@/contexts';
import { getDefaultPrivateRoute } from '@/helpers';
import { showToast, ToastType } from '@/helpers/toast';
import { SentoaLogo } from '@/resources';
import { useUpdateMfaStatusMutation } from '@/services';
import { TestIds } from '@/shared/test-ids';

export const MfaSetup = () => {
  const [t] = useTranslation('common');
  const navigate = useNavigate();
  const { currentUserData } = useAuth();
  const isMfaForced = currentUserData?.isMfaForced ?? false;

  const [step, setStep] = useState<'idle' | 'qr'>('idle');
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [totpSecret, setTotpSecret] = useState<TotpSecret | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(false);
  const [reAuthPassword, setReAuthPassword] = useState('');
  const [showReAuthPassword, setShowReAuthPassword] = useState(false);
  const [needsReAuth, setNeedsReAuth] = useState(false);
  const [reAuthStep, setReAuthStep] = useState<'password' | 'totp'>('password');
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const [reAuthTotpCode, setReAuthTotpCode] = useState('');

  useEffect(() => {
    if (pendingNavigation && currentUserData !== null && !currentUserData?.isMfaForced) {
      navigate(getDefaultPrivateRoute(currentUserData), { replace: true });
    }
  }, [pendingNavigation, currentUserData, navigate]);

  const { mutate: updateMfaStatus } = useUpdateMfaStatusMutation(queryClient, {
    onSuccess: () => {
      setPendingNavigation(true);
    },
    onError: () => showToast(ToastType.ERROR, t('mfaUpdateError'))
  });

  const startEnrollment = async () => {
    const currentUser = getAuth().currentUser;
    if (!currentUser) {
      return;
    }

    const session = await multiFactor(currentUser).getSession();
    const secret = await TotpMultiFactorGenerator.generateSecret(session);
    setTotpSecret(secret);

    const otpAuthUrl = secret.generateQrCodeUrl(currentUser.email ?? '', 'Sentoa');
    const dataUrl = await QRCode.toDataURL(otpAuthUrl);
    setQrCodeDataUrl(dataUrl);
    setStep('qr');
  };

  const handleStartSetup = async () => {
    setIsLoading(true);
    try {
      await startEnrollment();
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      if (code === 'auth/requires-recent-login' || code === 'auth/user-token-expired') {
        setNeedsReAuth(true);
      } else {
        showToast(ToastType.ERROR, t('mfaSetupError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleReAuth = async () => {
    setIsLoading(true);
    try {
      const currentUser = getAuth().currentUser;
      if (!currentUser?.email) {
        return;
      }

      const credential = EmailAuthProvider.credential(currentUser.email, reAuthPassword);
      await reauthenticateWithCredential(currentUser, credential);
      setNeedsReAuth(false);
      setReAuthPassword('');
      await startEnrollment();
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      if (code === 'auth/multi-factor-auth-required') {
        const auth = getAuth();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const resolver = getMultiFactorResolver(auth, err as any);
        setMfaResolver(resolver);
        setReAuthStep('totp');
      } else {
        showToast(ToastType.ERROR, t('mfaReAuthError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleReAuthTotp = async () => {
    if (!mfaResolver) {
      return;
    }
    setIsLoading(true);
    try {
      const totpFactor = mfaResolver.hints.find(
        (h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID
      );
      if (!totpFactor) {
        throw new Error('No TOTP factor');
      }

      const assertion = TotpMultiFactorGenerator.assertionForSignIn(totpFactor.uid, reAuthTotpCode);
      await mfaResolver.resolveSignIn(assertion);

      setNeedsReAuth(false);
      setReAuthPassword('');
      setReAuthTotpCode('');
      setReAuthStep('password');
      setMfaResolver(null);
      await startEnrollment();
    } catch {
      showToast(ToastType.ERROR, t('mfaInvalidCode'));
      setReAuthTotpCode('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyAndEnroll = async () => {
    if (!totpSecret) {
      return;
    }
    setIsLoading(true);
    try {
      const currentUser = getAuth().currentUser;
      if (!currentUser) {
        return;
      }

      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(totpSecret, totpCode);
      await multiFactor(currentUser).enroll(assertion, 'TOTP');
      showToast(ToastType.SUCCESS, t('mfaEnrolledSuccess'));
      updateMfaStatus({ isMfaEnabled: true });
    } catch {
      showToast(ToastType.ERROR, t('mfaInvalidCode'));
      setTotpCode('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    updateMfaStatus({ isMfaEnabled: false });
  };

  return (
    <div className='flex min-h-screen items-center justify-center bg-gray-100'>
      <div className='w-full max-w-md rounded-md bg-white p-8 shadow-md'>
        <div className='flex w-full justify-center pb-6'>
          <SentoaLogo className='h-10 w-44' />
        </div>
        <div className='mb-6 h-px w-full bg-primary-black opacity-20' />

        <div className='flex flex-col gap-4'>
          <Label text={t('mfaTitle')} type={LABEL_TYPE.H3_BOLD} className='!text-lg' />
          <Label
            text={t('mfaSetupDescription')}
            type={LABEL_TYPE.PARAGRAPH1}
            className='!text-secondary-gray'
          />

          {needsReAuth && (
            <MfaReAuthForm
              reAuthStep={reAuthStep}
              reAuthPassword={reAuthPassword}
              onReAuthPasswordChange={setReAuthPassword}
              showReAuthPassword={showReAuthPassword}
              onToggleShowReAuthPassword={() => setShowReAuthPassword((prev) => !prev)}
              isLoading={isLoading}
              onConfirm={handleReAuth}
              reAuthTotpCode={reAuthTotpCode}
              onReAuthTotpChange={setReAuthTotpCode}
              onVerifyTotp={handleReAuthTotp}
            />
          )}

          {!needsReAuth && step === 'idle' && (
            <div className='flex gap-3 pt-2'>
              <Button
                variant={ButtonVariant.PRIMARY}
                disabled={isLoading}
                handleOnClick={handleStartSetup}
                className='w-fit'
                data-testid={TestIds.mfa.setupEnableButton}>
                {t('mfaEnableButton')}
              </Button>
              {!isMfaForced && (
                <Button
                  variant={ButtonVariant.SECONDARY}
                  disabled={isLoading}
                  handleOnClick={handleSkip}
                  className='w-fit'
                  data-testid={TestIds.mfa.setupSkipButton}>
                  {t('mfaSkip')}
                </Button>
              )}
            </div>
          )}

          {!needsReAuth && step === 'qr' && (
            <MfaQrStep
              qrCodeDataUrl={qrCodeDataUrl}
              totpCode={totpCode}
              onTotpChange={setTotpCode}
              isLoading={isLoading}
              onVerify={handleVerifyAndEnroll}
              onSecondaryAction={isMfaForced ? undefined : handleSkip}
              secondaryActionLabel={isMfaForced ? undefined : t('mfaSkip')}
            />
          )}
        </div>
      </div>
    </div>
  );
};
